import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { ActionValidIcon } from '@hm-group/fabric-icons/icons/ActionValidIcon'
import { CloseIcon } from '@hm-group/fabric-icons/icons/CloseIcon'
import { ErrorIcon } from '@hm-group/fabric-icons/icons/ErrorIcon'
import { InfoIcon } from '@hm-group/fabric-icons/icons/InfoIcon'
import { WarningIcon } from '@hm-group/fabric-icons/icons/WarningIcon'
import cn from 'classnames'

import IconButton from 'components/Buttons/IconButton'
import BodyText from 'components/Typography/texts/BodyText'

import styles from './BannerNotification.module.scss'
import { BannerNotificationProps } from './types'

const BannerNotification: React.FC<BannerNotificationProps> = forwardRef(
  ({ closeLabel, message, severity = 'warning', testId }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const childrenRef = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(true)

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
      ref,
      () => containerRef.current,
    )

    const handleAnimationEnd = (): void => {
      setVisible(false)
    }

    const handleDismiss = (): void => {
      setVisible(false)
    }

    useEffect(() => {
      if (!containerRef.current) {
        return
      }

      if (visible) {
        containerRef.current.style.display = 'block'
        containerRef.current.style.height = 'auto'
        containerRef.current.style.opacity = '1'
      } else {
        containerRef.current.style.display = 'none'
        containerRef.current.style.height = '0px'
        containerRef.current.style.opacity = '0'
      }
    }, [])

    const applyNewStyles = useCallback(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      containerRef.current?.offsetHeight
    }, [containerRef, childrenRef])

    useEffect(() => {
      if (!childrenRef.current || !containerRef.current) {
        return () => {}
      }

      const { height } = containerRef.current.style
      const isAlreadyProcessed = (visible && height === 'auto') || (!visible && height === '0px')
      if (isAlreadyProcessed) {
        return () => {}
      }

      let timeoutId: number

      const transitionDuration = 300
      if (visible) {
        containerRef.current.style.display = 'block'
        containerRef.current.style.opacity = '1'
        applyNewStyles()
        containerRef.current.style.height = window.getComputedStyle(childrenRef.current).height

        timeoutId = window.setTimeout(() => {
          if (!containerRef.current) {
            return
          }
          handleAnimationEnd()

          containerRef.current.style.height = 'auto'
        }, transitionDuration)
      }

      if (!visible) {
        containerRef.current.style.opacity = '0'
        containerRef.current.style.height = window.getComputedStyle(childrenRef.current).height

        applyNewStyles()

        containerRef.current.style.height = '0px'
        timeoutId = window.setTimeout(() => {
          if (!containerRef.current) {
            return
          }
          handleAnimationEnd()

          containerRef.current.style.display = 'none'
        }, transitionDuration)
      }

      return () => {
        window.clearTimeout(timeoutId)
      }
    }, [visible])

    return (
      <div
        ref={containerRef}
        aria-live="assertive"
        role="alertdialog"
        aria-hidden={!visible}
        hidden={!visible}
        className={styles.animationContainer}
        data-testid={testId}
      >
        <div ref={childrenRef} className={styles.animationContainerInner}>
          <div className={cn(styles.banner, styles[`banner_${severity}`])}>
            {severity === 'error' ? (
              <ErrorIcon />
            ) : severity === 'warning' ? (
              <WarningIcon />
            ) : severity === 'info' ? (
              <InfoIcon />
            ) : (
              <ActionValidIcon />
            )}
            <BodyText className={styles.message}>{message}</BodyText>
            <IconButton
              className={styles.buttonClose}
              variant="ghost"
              iconElement={<CloseIcon />}
              onClick={handleDismiss}
            >
              {closeLabel}
            </IconButton>
          </div>
        </div>
      </div>
    )
  },
)

export default BannerNotification
